import { useState } from "react";
import { db } from "./firebase";
import { doc, setDoc } from "firebase/firestore";

const DEFAULT_CATEGORY_LABELS = { nutrition: "Nutrition", movement: "Movement", mindset: "Mindset" };

function AdminHabitOverride({ selectedAdminUser, defaultHabits, onSaved }) {
  const userHabits = selectedAdminUser.customHabits || defaultHabits;
  const userLabels = { ...DEFAULT_CATEGORY_LABELS, ...(selectedAdminUser.categoryLabels || {}) };

  const [habitDraft, setHabitDraft] = useState(null);
  const [habitSaving, setHabitSaving] = useState(false);

  if (!habitDraft) {
    return (
      <button
        type="button"
        onClick={() =>
          setHabitDraft({
            labels: { ...userLabels },
            nutrition: {
              low: userHabits.nutrition?.low?.habitName || "",
              main: userHabits.nutrition?.main?.habitName || "",
              high: userHabits.nutrition?.high?.habitName || "",
            },
            movement: {
              low: userHabits.movement?.low?.habitName || "",
              main: userHabits.movement?.main?.habitName || "",
              high: userHabits.movement?.high?.habitName || "",
            },
            mindset: {
              low: userHabits.mindset?.low?.habitName || "",
              main: userHabits.mindset?.main?.habitName || "",
              high: userHabits.mindset?.high?.habitName || "",
            },
          })
        }
        style={{
          padding: "10px 16px",
          borderRadius: "12px",
          border: "2px solid #bfdbfe",
          background: "#f5faff",
          color: "#1e3a5f",
          fontWeight: "bold",
          fontSize: "13px",
          cursor: "pointer",
        }}
      >
        ✏️ Edit Habit Names
      </button>
    );
  }

  const categories = [
    { key: "nutrition", emoji: "🍓" },
    { key: "movement", emoji: "🚶‍♀️" },
    { key: "mindset", emoji: "🌙" },
  ];

  const levels = [
    { level: "low", label: "Keep the Streak" },
    { level: "main", label: "Main Quest" },
    { level: "high", label: "Extra Credit" },
  ];

  async function handleSave() {
    setHabitSaving(true);
    try {
      const merged = {
        nutrition: {
          low: { ...defaultHabits.nutrition.low, ...(userHabits.nutrition?.low || {}), habitName: habitDraft.nutrition.low },
          main: { ...defaultHabits.nutrition.main, ...(userHabits.nutrition?.main || {}), habitName: habitDraft.nutrition.main },
          high: { ...defaultHabits.nutrition.high, ...(userHabits.nutrition?.high || {}), habitName: habitDraft.nutrition.high },
        },
        movement: {
          low: { ...defaultHabits.movement.low, ...(userHabits.movement?.low || {}), habitName: habitDraft.movement.low },
          main: { ...defaultHabits.movement.main, ...(userHabits.movement?.main || {}), habitName: habitDraft.movement.main },
          high: { ...defaultHabits.movement.high, ...(userHabits.movement?.high || {}), habitName: habitDraft.movement.high },
        },
        mindset: {
          low: { ...defaultHabits.mindset.low, ...(userHabits.mindset?.low || {}), habitName: habitDraft.mindset.low },
          main: { ...defaultHabits.mindset.main, ...(userHabits.mindset?.main || {}), habitName: habitDraft.mindset.main },
          high: { ...defaultHabits.mindset.high, ...(userHabits.mindset?.high || {}), habitName: habitDraft.mindset.high },
        },
      };
      const newLabels = { ...DEFAULT_CATEGORY_LABELS, ...habitDraft.labels };
      await setDoc(doc(db, "users", selectedAdminUser.id), { customHabits: merged, categoryLabels: newLabels }, { merge: true });
      setHabitDraft(null);
      if (onSaved) onSaved(merged, newLabels);
    } catch (err) {
      console.error("Error saving habit override:", err);
    } finally {
      setHabitSaving(false);
    }
  }

  return (
    <div style={{ display: "grid", gap: "12px" }}>
      {categories.map(({ key, emoji }) => (
        <div
          key={key}
          style={{ background: "#fff", borderRadius: "12px", padding: "12px", border: "1px solid #bfdbfe" }}
        >
          {/* Category name input */}
          <div style={{ marginBottom: "10px" }}>
            <div style={{ color: "#4b6b8a", fontSize: "11px", fontWeight: "bold", textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: "4px" }}>
              {emoji} Category Name
            </div>
            <input
              type="text"
              value={habitDraft.labels[key] || ""}
              onChange={(e) => {
                const val = e.target.value;
                setHabitDraft((prev) => ({ ...prev, labels: { ...prev.labels, [key]: val } }));
              }}
              style={{ width: "100%", padding: "8px 10px", borderRadius: "10px", border: "2px solid #bfdbfe", background: "#f0f9ff", color: "#1e3a5f", fontSize: "13px", fontWeight: "bold", boxSizing: "border-box" }}
            />
          </div>

          <div style={{ borderTop: "1px solid #dbeafe", paddingTop: "10px" }}>
            <div style={{ color: "#4b6b8a", fontSize: "11px", fontWeight: "bold", textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: "8px" }}>Habit Names</div>
            {levels.map(({ level, label: lvlLabel }) => (
              <div key={level} style={{ marginBottom: "8px" }}>
                <div style={{ color: "#6b8cb0", fontSize: "11px", marginBottom: "3px" }}>{lvlLabel}</div>
                <input
                  type="text"
                  value={habitDraft[key][level]}
                  onChange={(e) => {
                    const val = e.target.value;
                    setHabitDraft((prev) => ({ ...prev, [key]: { ...prev[key], [level]: val } }));
                  }}
                  style={{
                    width: "100%",
                    padding: "8px 10px",
                    borderRadius: "10px",
                    border: "1px solid #bfdbfe",
                    background: "#f8fbff",
                    color: "#1e3a5f",
                    fontSize: "13px",
                    boxSizing: "border-box",
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      ))}
      <div style={{ display: "flex", gap: "8px" }}>
        <button
          type="button"
          disabled={habitSaving}
          onClick={handleSave}
          style={{
            padding: "10px 16px",
            borderRadius: "12px",
            border: "none",
            background: "#38bdf8",
            color: "#fff",
            fontWeight: "bold",
            fontSize: "13px",
            cursor: "pointer",
          }}
        >
          {habitSaving ? "Saving..." : "Save"}
        </button>
        <button
          type="button"
          onClick={() => setHabitDraft(null)}
          style={{
            padding: "10px 16px",
            borderRadius: "12px",
            border: "2px solid #bfdbfe",
            background: "#f5faff",
            color: "#4b6b8a",
            fontWeight: "bold",
            fontSize: "13px",
            cursor: "pointer",
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

export default AdminHabitOverride;
